import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Mail, MapPin, Clock } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import { useSettings } from '../context/SettingsContext';

// Images from design guidelines
const images = {
  hero: 'https://images.unsplash.com/photo-1525286376485-60c84ee403d1?w=1600&q=80',
  commercial: 'https://images.unsplash.com/photo-1584259097630-4d5cb684a0ec?w=800&q=80',
  industrial: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80',
  residential: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  credo: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80',
};

/* ========== HERO SECTION ========== */
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center"
      style={{ background: '#3D3A38' }}
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={images.hero}
          alt="Architecture"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3D3A38] via-[#3D3A38]/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-20">
        <div className="max-w-3xl">
          <p 
            className="text-lg text-[#B8A066] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Licensed # 7018736
          </p>

          <p 
            className="text-xs tracking-[0.2em] uppercase mb-6 text-[#B8A066]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            A Division of Aozat General Contractors
          </p>
          
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] leading-[1.1] mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Commercial, Industrial & Residential Electrical — <br />
            <span className="italic text-[#B8A066]">Done Right.</span>
          </h1>

          <p 
            className="text-lg text-[#F5F1EC]/70 mb-10 max-w-xl leading-relaxed"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}
          >
            Code-compliant installs, responsive service, and clean project execution across the Greater Toronto Area since 2014.
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="hero-cta"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F5F1EC]/40">
        <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: 'Outfit, sans-serif' }}>Scroll</p>
      </div>
    </section>
  );
};

/* ========== CREDO SECTION ========== */
const CredoSection = () => {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2" data-testid="credo-section">
      {/* Image */}
      <div className="relative h-[400px] lg:h-auto">
        <img 
          src={images.credo}
          alt="Construction site"
          className="w-full h-full object-cover grayscale"
        />
      </div>

      {/* Content */}
      <div className="bg-[#F5F1EC] flex items-center">
        <div className="p-12 md:p-16 lg:p-20">
          <p className="section-label mb-6">Our Credo</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-[#3D3A38] leading-[1.2] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Where trust meets <span className="italic text-[#B8A066]">quality.</span><br />
            We are Electricians who <span className="italic text-[#B8A066]">care.</span>
          </h2>
          <p 
            className="text-sm text-[#5C5855] italic"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            — Aozat Electrical, since 2014
          </p>
        </div>
      </div>
    </section>
  );
};

/* ========== SERVICES SECTION ========== */
const ServicesSection = () => {
  const services = [
    {
      numeral: 'i',
      title: 'Commercial',
      tagline: 'Ground-up & fit-out, delivered on schedule.',
      description: 'Retail, hospitality, restaurants, office, mixed-use. From shell construction to tier-one tenant improvements, we manage every trade, every inspection, every deliverable.',
      items: ['Restaurants & hospitality', 'Retail & franchise', 'Office & mixed-use', 'Tenant improvements'],
      image: images.commercial,
      link: '/services#commercial',
    },
    {
      numeral: 'ii',
      title: 'Residential',
      tagline: 'Custom homes and multi-unit, finished to a standard.',
      description: 'Custom homes, townhomes, low-rise condominiums, and purpose-built rentals. We work alongside owners, architects, and developers from foundation through occupancy.',
      items: ['Custom homes', 'Townhomes & low-rise', 'Multi-unit residential', 'Renovation & additions'],
      image: images.residential,
      link: '/services#residential',
    },
    {
      numeral: 'iii',
      title: 'Industrial',
      tagline: 'Built for the floor it has to perform on.',
      description: 'Distribution centres, cold storage, warehouses, manufacturing facilities. Heavy electrical, motor controls, high-voltage systems. The work that keeps a business moving.',
      items: ['Warehousing & distribution', 'Cold storage', 'Manufacturing facilities', 'High-voltage systems'],
      image: images.industrial,
      link: '/services#industrial',
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F5F1EC]" data-testid="services-section">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">Services</p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-[#3D3A38]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Three sectors.<br />
            <span className="italic text-[#B8A066]">One standard.</span>
          </h2>
          <p 
            className="mt-6 text-[#5C5855] max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            We do not subcontract our judgment. We manage every project with care, expertise, and dedication — whatever the sector.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {services.map((service) => (
            <div key={service.numeral} className="group">
              {/* Image */}
              <div className="relative h-[200px] mb-8 overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
              </div>

              {/* Content */}
              <div className="flex items-start gap-3 mb-4">
                <span className="roman-numeral">{service.numeral}</span>
                <h3 
                  className="text-2xl md:text-3xl text-[#3D3A38]"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
                >
                  {service.title}
                </h3>
              </div>

              <p 
                className="text-[#B8A066] italic mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {service.tagline}
              </p>

              <p 
                className="text-sm text-[#5C5855] mb-6 leading-relaxed"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {service.description}
              </p>

              {/* Service Items */}
              <ul className="space-y-2 mb-6">
                {service.items.map((item, idx) => (
                  <li 
                    key={idx}
                    className="text-xs tracking-[0.1em] uppercase text-[#5C5855] flex items-center gap-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span className="w-4 h-px bg-[#B8A066]" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link 
                to={service.link}
                className="text-xs tracking-[0.15em] uppercase text-[#3D3A38] hover:text-[#B8A066] transition-colors duration-300 inline-flex items-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Learn More <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== STATS MARQUEE ========== */
const StatsMarquee = () => {
  const stats = [
    { value: 'Since 2014', label: 'In the trade' },
    { value: '240+', label: 'Projects delivered' },
    { value: '$10M+', label: 'Active pipeline' },
    { value: '24/7', label: 'Emergency service' },
  ];

  const values = ['Discipline', 'Craft', 'Accountability', 'Care', 'Quality', 'Integrity'];

  return (
    <section className="py-16 bg-[#3D3A38]" data-testid="stats-marquee">
      <Marquee speed={40} gradient={false}>
        {stats.map((stat, i) => (
          <div key={i} className="flex items-center px-12">
            <span 
              className="text-3xl text-[#F5F1EC] mr-3"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
            >
              {stat.value}
            </span>
            <span 
              className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/50"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {stat.label}
            </span>
            <span className="mx-8 text-[#B8A066] italic" style={{ fontFamily: 'Cormorant Garamond, serif' }}>✦</span>
            <span 
              className="text-sm italic text-[#B8A066]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {values[i % values.length]}
            </span>
            <span className="mx-8 text-[#B8A066] italic" style={{ fontFamily: 'Cormorant Garamond, serif' }}>✦</span>
          </div>
        ))}
      </Marquee>
    </section>
  );
};

/* ========== CONTACT SECTION ========== */
const ContactSection = () => {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          service: form.type,
          message: form.message,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        setForm({ name: '', company: '', email: '', phone: '', type: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
    setSubmitting(false);
  };

  return (
    <section className="py-24 md:py-32 bg-[#3D3A38]" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <div>
            <p className="section-label mb-4 text-[#F5F1EC]/60">Inquire</p>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl text-[#F5F1EC] mb-12"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              Tell us what you are <span className="italic text-[#B8A066]">building.</span>
            </h2>

            {submitted ? (
              <div className="text-center py-12">
                <p className="text-xl text-[#F5F1EC] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Thank you for your inquiry.
                </p>
                <p className="text-[#F5F1EC]/60" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  We respond back in 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Your Name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="First and last"
                      style={{ background: 'transparent' }}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Company</label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="Developer or owner"
                      style={{ background: 'transparent' }}
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="name@company.com"
                      style={{ background: 'transparent' }}
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="+1"
                      style={{ background: 'transparent' }}
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label text-[#F5F1EC]/60">Project Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066] cursor-pointer"
                    style={{ background: 'transparent' }}
                    data-testid="contact-type"
                  >
                    <option value="" className="text-[#3D3A38]">Select one</option>
                    <option value="Commercial" className="text-[#3D3A38]">Commercial</option>
                    <option value="Residential" className="text-[#3D3A38]">Residential</option>
                    <option value="Industrial" className="text-[#3D3A38]">Industrial</option>
                    <option value="Not sure yet" className="text-[#3D3A38]">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <label className="form-label text-[#F5F1EC]/60">Brief</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066] resize-none"
                    placeholder="Site, square footage, timeline, anything we should know."
                    style={{ background: 'transparent' }}
                    data-testid="contact-message"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <p className="text-xs text-[#F5F1EC]/40 italic" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Every note is read personally.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary ml-auto"
                    data-testid="contact-submit"
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'} <ArrowRight size={14} className="inline ml-2" />
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="lg:pl-12">
            <h3 
              className="text-2xl text-[#F5F1EC] mb-12"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              Or, if you prefer the long way around.
            </h3>

            <div className="space-y-8">
              <div className="flex gap-8">
                <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/40 w-24" style={{ fontFamily: 'Outfit, sans-serif' }}>Office</p>
                <p className="text-sm text-[#F5F1EC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {settings.address}
                </p>
              </div>

              <div className="flex gap-8">
                <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/40 w-24" style={{ fontFamily: 'Outfit, sans-serif' }}>Hours</p>
                <div className="text-sm text-[#F5F1EC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <p>{settings.business_hours}</p>
                  <p className="text-[#F5F1EC]/60 mt-1">{settings.emergency_hours}</p>
                </div>
              </div>

              <div className="flex gap-8">
                <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/40 w-24" style={{ fontFamily: 'Outfit, sans-serif' }}>Direct</p>
                <div className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <a href={`mailto:${settings.email}`} className="block text-[#F5F1EC] hover:text-[#B8A066] transition-colors">
                    {settings.email}
                  </a>
                  <a href={`tel:${settings.phone}`} className="block text-[#F5F1EC] hover:text-[#B8A066] transition-colors mt-1">
                    {settings.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-8">
                <p className="text-xs tracking-[0.15em] uppercase text-[#F5F1EC]/40 w-24" style={{ fontFamily: 'Outfit, sans-serif' }}>Compliance</p>
                <div className="text-sm text-[#F5F1EC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <p>Licensed # 7018736</p>
                  <p className="text-[#F5F1EC]/60 mt-1">ESA Certified</p>
                </div>
              </div>
            </div>

            {/* Gold Logo Mark */}
            <div className="mt-16 opacity-30">
              <img 
                src="https://customer-assets.emergentagent.com/job_aozat-power/artifacts/19tmgxv4_image.png"
                alt="AOZAT"
                className="h-32 w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ========== MAIN PAGE ========== */
export default function HomePage() {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <CredoSection />
      <ServicesSection />
      <StatsMarquee />
      <ContactSection />
    </main>
  );
}
